<template>
  <div>
    <p class="tw-text-muted tw-mb-4 tw-text-sm">
      Brokurz Website Builder <strong>Add MLS</strong> applications land here as
      <code>pending</code>. Review the request, finish vendor credentials
      (<router-link to="/vendors">Vendors hub</router-link>), then
      <strong>Activate</strong>. After activation + sync, that brokerage/agent site
      shows live listings instead of demo data.
    </p>

    <Message v-if="!warehouseConfigured" severity="warn" :closable="false" class="tw-mb-4">
      Set <code>LISTINGS_SUPABASE_URL</code> and <code>LISTINGS_SUPABASE_SERVICE_ROLE_KEY</code>
      in <code>.env</code>, then restart the API.
    </Message>

    <div class="kpi-row tw-mb-4">
      <div class="kpi-card">
        <div class="kpi-label">Pending</div>
        <div class="kpi-value">{{ countByStatus('pending') }}</div>
        <div class="kpi-hint">Needs ops activation</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Active</div>
        <div class="kpi-value">{{ countByStatus('active') }}</div>
        <div class="kpi-hint">Live on Brokurz sites</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Suspended</div>
        <div class="kpi-value">{{ countByStatus('suspended') }}</div>
        <div class="kpi-hint">Temporarily off</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">All</div>
        <div class="kpi-value">{{ allRows.length }}</div>
        <div class="kpi-hint">In this status filter</div>
      </div>
    </div>

    <Card class="tw-mb-4">
      <template #title>Filters</template>
      <template #content>
        <div class="filters">
          <InputText
            v-model="q"
            class="tw-w-full"
            placeholder="Search board, brokerage, contact, membership #"
            @input="onFilterInput"
            @keydown.enter.native.prevent="applyFiltersNow"
          />
          <Dropdown
            v-model="statusFilter"
            :options="statusFilterOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Status"
            class="tw-w-full"
            @change="load"
          />
          <Button label="Refresh" icon="pi pi-refresh" :loading="loading" @click="load" />
        </div>
      </template>
    </Card>

    <Message v-if="error" severity="error" :closable="false" class="tw-mb-4">{{ error }}</Message>
    <Message v-if="actionMessage" :severity="actionOk ? 'success' : 'warn'" :closable="true" class="tw-mb-4">
      {{ actionMessage }}
    </Message>

    <Card>
      <template #title>MLS applications from Brokurz</template>
      <template #subtitle>Requested → activate here → sync → listings appear on their website</template>
      <template #content>
        <DataTable
          :value="rows"
          :loading="loading"
          dataKey="id"
          :paginator="true"
          :rows="15"
          responsiveLayout="scroll"
          emptyMessage="No applications in this filter."
        >
          <Column header="Requested" style="min-width: 9rem">
            <template #body="{ data }">
              <div class="cell-stack">
                <span>{{ formatWhen(data.requestedAt || data.createdAt) }}</span>
                <span class="tw-text-muted tw-text-xs">{{ relativeWhen(data.requestedAt || data.createdAt) }}</span>
              </div>
            </template>
          </Column>
          <Column header="Status" style="min-width: 7rem">
            <template #body="{ data }">
              <span class="status-pill" :data-status="data.status">{{ data.status }}</span>
            </template>
          </Column>
          <Column header="Board" style="min-width: 11rem">
            <template #body="{ data }">
              <div class="cell-stack">
                <strong>{{ data.feedDisplayName || data.mlsBoardName || '—' }}</strong>
                <span class="tw-text-muted tw-text-xs">
                  {{ data.mlsBoardAcronym || data.feedSlug || '' }}
                  <template v-if="data.feedVendor"> · {{ data.feedVendor }}</template>
                  <template v-if="data.feedStatus"> · feed {{ data.feedStatus }}</template>
                </span>
              </div>
            </template>
          </Column>
          <Column header="Who" style="min-width: 12rem">
            <template #body="{ data }">
              <div class="cell-stack">
                <strong>{{ data.brokerageName || data.contactName || '—' }}</strong>
                <span class="tw-text-muted tw-text-xs">{{ audienceLabel(data) }}</span>
                <span v-if="data.contactEmail" class="tw-text-xs">{{ data.contactEmail }}</span>
              </div>
            </template>
          </Column>
          <Column header="Membership / office" style="min-width: 10rem">
            <template #body="{ data }">
              <div class="cell-stack">
                <span v-if="data.membershipNumber">MLS # {{ data.membershipNumber }}</span>
                <span v-else class="tw-text-muted">No membership #</span>
                <span class="tw-text-muted tw-text-xs">Office MLS: {{ data.officeMlsId || '—' }}</span>
              </div>
            </template>
          </Column>
          <Column header="MLS setup" style="min-width: 9rem">
            <template #body="{ data }">
              <div class="cell-stack">
                <span class="status-pill" :data-status="billingPill(data.billingStatus)">{{ billingLabel(data.billingStatus) }}</span>
                <span class="tw-text-muted tw-text-xs">{{ feeSummary(data) }}</span>
              </div>
            </template>
          </Column>
          <Column header="Website" style="min-width: 10rem">
            <template #body="{ data }">
              <div class="cell-stack">
                <span>{{ data.websiteLabel || '—' }}</span>
                <a
                  v-if="data.websiteUrl"
                  :href="data.websiteUrl"
                  target="_blank"
                  rel="noopener"
                  class="tw-text-xs"
                >{{ data.websiteUrl }}</a>
              </div>
            </template>
          </Column>
          <Column header="" style="min-width: 8rem">
            <template #body="{ data }">
              <Button
                type="button"
                label="Open"
                class="p-button-text p-button-sm"
                @click.stop="openDetail(data)"
              />
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>

    <Dialog
      :visible.sync="detailVisible"
      modal
      dismissableMask
      closable
      :style="{ width: 'min(72rem, 96vw)' }"
      :contentStyle="{ maxHeight: '78vh', overflow: 'auto' }"
      :header="detailHeader"
      class="detail-dialog"
      @hide="onDialogHide"
    >
      <div v-if="selected">
      <p class="tw-text-muted tw-text-xs tw-mb-3">
        {{ selected.feedDisplayName }}
        <template v-if="selected.feedVendor"> · {{ selected.feedVendor }}</template>
        · tenant <code>{{ selected.brokurzTenantId }}</code>
      </p>
        <div class="detail-grid">
          <div><span class="lbl">Status</span><span class="status-pill" :data-status="selected.status">{{ selected.status }}</span></div>
          <div><span class="lbl">Requested</span>{{ formatWhen(selected.requestedAt || selected.createdAt) }}</div>
          <div><span class="lbl">Activated</span>{{ selected.activatedAt ? formatWhen(selected.activatedAt) : '—' }}</div>
          <div><span class="lbl">Audience</span>{{ audienceLabel(selected) }}</div>
          <div><span class="lbl">Brokerage</span>{{ selected.brokerageName || '—' }}</div>
          <div><span class="lbl">Brokerage email</span>{{ selected.brokerageEmail || '—' }}</div>
          <div><span class="lbl">Brokerage phone</span>{{ selected.brokeragePhone || '—' }}</div>
          <div><span class="lbl">Contact</span>{{ selected.contactName || '—' }}</div>
          <div><span class="lbl">Contact email</span>{{ selected.contactEmail || '—' }}</div>
          <div><span class="lbl">Contact phone</span>{{ selected.contactPhone || '—' }}</div>
          <div><span class="lbl">Office name</span>{{ selected.officeName || '—' }}</div>
          <div><span class="lbl">Office MLS ID</span>{{ selected.officeMlsId || '—' }}</div>
          <div><span class="lbl">Membership #</span>{{ selected.membershipNumber || '—' }}</div>
          <div><span class="lbl">License #</span>{{ selected.licenseNumber || '—' }}</div>
          <div><span class="lbl">Website</span>{{ selected.websiteLabel || '—' }}</div>
          <div><span class="lbl">Website URL</span>
            <a v-if="selected.websiteUrl" :href="selected.websiteUrl" target="_blank" rel="noopener">{{ selected.websiteUrl }}</a>
            <span v-else>—</span>
          </div>
          <div><span class="lbl">Automation</span>{{ selected.automationStatus || 'applied' }}</div>
          <div><span class="lbl">Grid invite sent</span>{{ selected.gridInviteSentAt ? formatWhen(selected.gridInviteSentAt) : '—' }}</div>
          <div><span class="lbl">Grid approved</span>{{ selected.gridApprovedAt ? formatWhen(selected.gridApprovedAt) : '—' }}</div>
          <div><span class="lbl">Brokurz notified</span>{{ selected.brokurzNotifiedAt ? formatWhen(selected.brokurzNotifiedAt) : '—' }}</div>
          <div><span class="lbl">MLS setup</span><span class="status-pill" :data-status="billingPill(selected.billingStatus)">{{ billingLabel(selected.billingStatus) }}</span></div>
          <div><span class="lbl">Paid</span>{{ selected.billingPaidAt ? formatWhen(selected.billingPaidAt) : '—' }}</div>
          <div class="span-2" v-if="selected.offPlatformBroker">
            <Message severity="info" :closable="false">
              Solo agent — sponsoring broker is <strong>off Brokurz</strong>. Use brokerage name/email above for vendor paperwork.
            </Message>
          </div>
          <div class="span-2">
            <Message severity="info" :closable="false">
              {{ vendorPathCopy(selected) }}
            </Message>
          </div>
          <div class="span-2">
            <Message severity="info" :closable="false">
              After the application lands, send MLS setup here. The broker/agent pays in
              <strong>Website Builder → MLS Connections</strong>. Unpaid setup only turns
              listings off — Brokurz stays available.
            </Message>
          </div>
          <div>
            <label class="field-label">One-time setup ($)</label>
            <InputText v-model="activationFeeDollars" class="tw-w-full" placeholder="e.g. 250" />
          </div>
          <div>
            <label class="field-label">Monthly ($)</label>
            <InputText v-model="monthlyFeeDollars" class="tw-w-full" placeholder="e.g. 85" />
          </div>
          <div class="span-2">
            <label class="field-label">Ops notes</label>
            <Textarea v-model="opsNotes" rows="2" class="tw-w-full" placeholder="Vendor ticket #, Grid subscription, etc." />
          </div>
        </div>

        <div class="actions tw-mt-4">
          <Button
            label="Save amounts"
            icon="pi pi-dollar"
            class="p-button-outlined"
            :loading="acting"
            @click="runBilling('set_fees')"
          />
          <Button
            :label="selected.billingStatus === 'payment_due' ? 'Resend payment request' : 'Send payment request'"
            icon="pi pi-send"
            class="p-button-outlined"
            :loading="acting"
            @click="runBilling(selected.billingStatus === 'payment_due' ? 'resend' : 'send')"
          />
          <Button
            v-if="selected.billingStatus !== 'waived' && selected.billingStatus !== 'active'"
            label="Waive (no charge)"
            class="p-button-text"
            :loading="acting"
            @click="runBilling('waive')"
          />
          <Button
            label="1 · Prepare vendor packet"
            icon="pi pi-file"
            class="p-button-outlined"
            :loading="acting"
            @click="runAuto('prepare')"
          />
          <Button
            :label="vendorAdminLabel(selected)"
            icon="pi pi-external-link"
            class="p-button-outlined"
            @click="openVendorAdmin"
          />
          <Button
            label="2 · Mark invite sent"
            icon="pi pi-send"
            class="p-button-outlined"
            :loading="acting"
            @click="runAuto('mark_invite_sent')"
          />
          <Button
            v-if="selected.status === 'pending' || selected.status === 'suspended'"
            label="3 · Approved → Activate + notify"
            icon="pi pi-check"
            class="p-button-success"
            :loading="acting"
            :disabled="billingBlocksActivate(selected)"
            :title="billingBlocksActivate(selected) ? 'Payment must be current (or waived) before the feed goes live.' : ''"
            @click="runAuto('mark_approved_and_activate')"
          />
          <Button
            v-if="selected.status === 'active'"
            label="Re-notify Brokurz"
            class="p-button-outlined"
            :loading="acting"
            @click="runAuto('notify_brokurz')"
          />
          <Button
            v-if="selected.status === 'active'"
            label="Suspend"
            class="p-button-outlined p-button-danger"
            :loading="acting"
            @click="setStatus('suspended')"
          />
          <Button
            label="Copy packet"
            class="p-button-text"
            @click="copyPacket"
          />
          <Button
            label="Close"
            class="p-button-text"
            @click="closeDetail"
          />
        </div>
        <p class="tw-text-muted tw-text-xs tw-mt-3">
          After activate: ensure <code>credential_ref</code> on Boards, run sync.
          Brokurz is notified automatically via the warehouse (no webhook setup).
        </p>
      </div>
    </Dialog>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import Card from 'primevue/card'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Dropdown from 'primevue/dropdown'
import Message from 'primevue/message'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Dialog from 'primevue/dialog'

const STATUS_QUERY = gql`
  query ListingsWarehouseStatus {
    listingsWarehouseStatus { configured }
  }
`

const APPS_QUERY = gql`
  query BrokurzMlsApplications($status: ConnectionStatus, $q: String) {
    brokurzMlsApplications(status: $status, q: $q) {
      id status useCase brokurzTenantId feedId feedSlug feedDisplayName feedStatus
      feedVendor vendorAdminUrl
      mlsBoardName mlsBoardAcronym officeMlsIds agreementSignedAt activatedAt notes
      createdAt updatedAt requestedAt audience contactName contactEmail contactPhone
      officeMlsId officeName brokerageName brokerageEmail brokeragePhone
      membershipNumber licenseNumber websiteLabel websiteUrl offPlatformBroker
      appliedByUserId source automationStatus automationStep gridAdminUrl
      gridInviteSentAt gridApprovedAt brokurzNotifiedAt
      billingStatus activationFeeCents monthlyFeeCents billingRequestedAt
      billingPaidAt billingCheckoutUrl
    }
  }
`

const SET_STATUS = gql`
  mutation SetBrokurzMlsApplicationStatus(
    $id: ID!
    $status: ConnectionStatus!
    $activateFeed: Boolean
    $notes: String
  ) {
    setBrokurzMlsApplicationStatus(
      id: $id
      status: $status
      activateFeed: $activateFeed
      notes: $notes
    ) {
      id status activatedAt feedStatus automationStatus
    }
  }
`

const RUN_AUTO = gql`
  mutation RunMlsApplicationAutomation(
    $id: ID!
    $action: String!
    $activateFeed: Boolean
    $notes: String
  ) {
    runMlsApplicationAutomation(
      id: $id
      action: $action
      activateFeed: $activateFeed
      notes: $notes
    ) {
      id status activatedAt feedStatus automationStatus automationStep
      gridInviteSentAt gridApprovedAt brokurzNotifiedAt gridAdminUrl
      billingStatus activationFeeCents monthlyFeeCents billingRequestedAt
      billingPaidAt billingCheckoutUrl
    }
  }
`

const SEND_BILLING = gql`
  mutation SendBrokurzMlsApplicationBilling(
    $id: ID!
    $action: String!
    $activationFeeDollars: Float
    $monthlyFeeDollars: Float
    $notes: String
  ) {
    sendBrokurzMlsApplicationBilling(
      id: $id
      action: $action
      activationFeeDollars: $activationFeeDollars
      monthlyFeeDollars: $monthlyFeeDollars
      notes: $notes
    ) {
      id billingStatus activationFeeCents monthlyFeeCents billingRequestedAt
      billingPaidAt billingCheckoutUrl
    }
  }
`

export default {
  name: 'ApplicationsPage',
  components: { Card, Button, InputText, Textarea, Dropdown, Message, DataTable, Column, Dialog },
  data() {
    return {
      rows: [],
      allRows: [],
      loading: false,
      acting: false,
      error: null,
      actionMessage: null,
      actionOk: false,
      warehouseConfigured: false,
      q: '',
      statusFilter: 'pending',
      selected: null,
      detailVisible: false,
      opsNotes: '',
      activationFeeDollars: '',
      monthlyFeeDollars: '',
      filterTimer: null,
      statusFilterOptions: [
        { label: 'Pending', value: 'pending' },
        { label: 'Active', value: 'active' },
        { label: 'Suspended', value: 'suspended' },
        { label: 'Revoked', value: 'revoked' },
        { label: 'All', value: null },
      ],
    }
  },
  computed: {
    detailHeader() {
      if (!this.selected) return 'Application'
      return [
        this.selected.feedDisplayName || 'Application',
        this.selected.brokerageName || this.selected.contactName || null,
      ]
        .filter(Boolean)
        .join(' · ')
    },
  },
  mounted() {
    this.load()
  },
  beforeDestroy() {
    if (this.filterTimer) clearTimeout(this.filterTimer)
  },
  methods: {
    countByStatus(status) {
      return this.allRows.filter((r) => r.status === status).length
    },
    feedVendorKey(row) {
      return String((row && row.feedVendor) || '').toLowerCase()
    },
    vendorAdminLabel(row) {
      const v = this.feedVendorKey(row)
      if (v === 'trestle') return 'Open Trestle admin'
      if (v === 'bridge_interactive' || v === 'bridge') return 'Open Bridge admin'
      return 'Open MLS Grid admin'
    },
    vendorAdminHref(row) {
      if (row && row.vendorAdminUrl) return row.vendorAdminUrl
      if (row && row.gridAdminUrl) return row.gridAdminUrl
      const v = this.feedVendorKey(row)
      if (v === 'trestle') return 'https://trestle.corelogic.com/SubscriptionWizard'
      if (v === 'bridge_interactive' || v === 'bridge') return 'https://bridgedataoutput.com/'
      return 'https://app.mlsgrid.com/admin'
    },
    vendorPathCopy(row) {
      const v = this.feedVendorKey(row)
      if (v === 'trestle') {
        return 'Seamless path: 1) Prepare packet → 2) Open Trestle and send the authorization → 3) Mark invite sent → 4) When approved, activate + notify Brokurz.'
      }
      if (v === 'bridge_interactive' || v === 'bridge') {
        return 'Seamless path: 1) Prepare packet → 2) Open Bridge and request the dataset → 3) Mark invite sent → 4) When approved, activate + notify Brokurz.'
      }
      return 'Seamless path: 1) Prepare packet → 2) Open Grid admin and send a Data License Request → 3) Mark invite sent → 4) When approved, activate + notify Brokurz. Grid emails the broker from notify@mlsgrid.com.'
    },
    billingLabel(status) {
      const s = String(status || 'none')
      if (s === 'payment_due') return 'Payment due'
      if (s === 'active') return 'Current'
      if (s === 'past_due') return 'Past due'
      if (s === 'waived') return 'Waived'
      if (s === 'draft') return 'Amounts set'
      if (s === 'canceled') return 'Canceled'
      return 'Not sent'
    },
    billingPill(status) {
      const s = String(status || 'none')
      if (s === 'active' || s === 'waived') return 'active'
      if (s === 'payment_due' || s === 'draft') return 'pending'
      if (s === 'past_due' || s === 'canceled') return 'suspended'
      return 'pending'
    },
    feeSummary(row) {
      const setup = Number(row.activationFeeCents || 0)
      const monthly = Number(row.monthlyFeeCents || 0)
      if (!setup && !monthly) return 'No amounts'
      const parts = []
      if (setup) parts.push(`$${(setup / 100).toFixed(0)} setup`)
      if (monthly) parts.push(`$${(monthly / 100).toFixed(0)}/mo`)
      return parts.join(' · ')
    },
    billingBlocksActivate(row) {
      const s = String((row && row.billingStatus) || 'none')
      return s === 'draft' || s === 'payment_due' || s === 'past_due' || s === 'canceled'
    },
    hydrateFees(row) {
      if (!row) {
        this.activationFeeDollars = ''
        this.monthlyFeeDollars = ''
        return
      }
      this.activationFeeDollars =
        row.activationFeeCents > 0 ? String(row.activationFeeCents / 100) : ''
      this.monthlyFeeDollars = row.monthlyFeeCents > 0 ? String(row.monthlyFeeCents / 100) : ''
    },
    parseFee(value) {
      const n = Number(String(value || '').replace(/[^0-9.]/g, ''))
      return Number.isFinite(n) ? n : 0
    },
    audienceLabel(row) {
      if (row.offPlatformBroker || row.audience === 'solo_agent') return 'Solo agent (broker off-platform)'
      if (row.audience === 'agent_under_brokerage') return 'Agent under Brokurz brokerage'
      if (row.audience === 'brokerage') return 'Brokerage apply'
      return row.audience || 'Brokurz apply'
    },
    formatWhen(iso) {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleString()
      } catch {
        return iso
      }
    },
    relativeWhen(iso) {
      if (!iso) return ''
      const t = new Date(iso).getTime()
      if (!Number.isFinite(t)) return ''
      const mins = Math.round((Date.now() - t) / 60000)
      if (mins < 1) return 'just now'
      if (mins < 60) return `${mins}m ago`
      const hrs = Math.round(mins / 60)
      if (hrs < 48) return `${hrs}h ago`
      const days = Math.round(hrs / 24)
      return `${days}d ago`
    },
    onFilterInput() {
      if (this.filterTimer) clearTimeout(this.filterTimer)
      this.filterTimer = setTimeout(() => this.applyFiltersNow(), 120)
    },
    applyFiltersNow() {
      if (this.filterTimer) {
        clearTimeout(this.filterTimer)
        this.filterTimer = null
      }
      const needle = String(this.q || '').trim().toLowerCase()
      let next = Array.isArray(this.allRows) ? this.allRows.slice() : []
      if (needle) {
        next = next.filter((r) => {
          const hay = [
            r.feedDisplayName,
            r.feedSlug,
            r.mlsBoardName,
            r.mlsBoardAcronym,
            r.brokerageName,
            r.contactName,
            r.contactEmail,
            r.membershipNumber,
            r.officeMlsId,
            r.brokurzTenantId,
            r.websiteLabel,
            r.websiteUrl,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
          return hay.includes(needle)
        })
      }
      this.rows = next
    },
    onSelect() {
      this.opsNotes = (this.selected && this.selected.notes) || ''
      this.hydrateFees(this.selected)
    },
    openDetail(row) {
      if (!row) return
      this.selected = row
      this.opsNotes = row.notes || ''
      this.hydrateFees(row)
      this.detailVisible = true
    },
    closeDetail() {
      this.detailVisible = false
      this.selected = null
    },
    onDialogHide() {
      this.detailVisible = false
      this.selected = null
    },
    openVendorAdmin() {
      const url = this.vendorAdminHref(this.selected)
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    async load() {
      this.loading = true
      this.error = null
      try {
        const statusRes = await this.$apollo.query({
          query: STATUS_QUERY,
          fetchPolicy: 'network-only',
        })
        this.warehouseConfigured = Boolean(
          statusRes.data?.listingsWarehouseStatus?.configured
        )
        if (!this.warehouseConfigured) {
          this.allRows = []
          this.rows = []
          return
        }
        const { data } = await this.$apollo.query({
          query: APPS_QUERY,
          variables: {
            status: this.statusFilter || null,
            q: null,
          },
          fetchPolicy: 'network-only',
        })
        this.allRows = data?.brokurzMlsApplications || []
        this.applyFiltersNow()
        if (this.selected) {
          const next = this.rows.find((r) => r.id === this.selected.id)
            || this.allRows.find((r) => r.id === this.selected.id)
          this.selected = next || null
          this.detailVisible = Boolean(this.selected)
          this.hydrateFees(this.selected)
        }
      } catch (err) {
        this.error = err.message || 'Failed to load applications'
        this.allRows = []
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    async activate(activateFeed) {
      await this.setStatus('active', activateFeed)
    },
    async runBilling(action) {
      if (!this.selected) return
      this.acting = true
      this.actionMessage = null
      try {
        await this.$apollo.mutate({
          mutation: SEND_BILLING,
          variables: {
            id: this.selected.id,
            action,
            activationFeeDollars: this.parseFee(this.activationFeeDollars),
            monthlyFeeDollars: this.parseFee(this.monthlyFeeDollars),
            notes: this.opsNotes || null,
          },
        })
        this.actionOk = true
        const labels = {
          set_fees: 'Amounts saved. Send a payment request when you are ready.',
          send: 'Payment request sent. It will appear in Website Builder → MLS Connections.',
          resend: 'Payment request re-queued for Website Builder.',
          waive: 'Billing waived. You can activate the feed without charging.',
        }
        this.actionMessage = labels[action] || 'MLS billing updated.'
        await this.load()
      } catch (err) {
        this.actionOk = false
        this.actionMessage = err.message || 'Billing update failed'
      } finally {
        this.acting = false
      }
    },
    async runAuto(action) {
      if (!this.selected) return
      this.acting = true
      this.actionMessage = null
      try {
        await this.$apollo.mutate({
          mutation: RUN_AUTO,
          variables: {
            id: this.selected.id,
            action,
            activateFeed: true,
            notes: this.opsNotes || null,
          },
        })
        this.actionOk = true
        const labels = {
          prepare: 'Vendor packet prepared — open the vendor admin and send the authorization.',
          mark_invite_sent: 'Marked invite sent. Waiting on broker signature + MLS approval.',
          mark_approved_and_activate: 'Activated + notified Brokurz. Run sync next.',
          notify_brokurz: 'Brokurz notified.',
        }
        this.actionMessage = labels[action] || `Automation “${action}” completed.`
        await this.load()
      } catch (err) {
        this.actionOk = false
        this.actionMessage = err.message || 'Automation failed'
      } finally {
        this.acting = false
      }
    },
    async copyPacket() {
      if (!this.selected) return
      const s = this.selected
      const text = [
        `Board: ${s.feedDisplayName || s.mlsBoardName}`,
        `Acronym: ${s.mlsBoardAcronym || ''}`,
        `Vendor: ${s.feedVendor || ''}`,
        `Brokerage: ${s.brokerageName || ''}`,
        `Brokerage email: ${s.brokerageEmail || ''}`,
        `Contact: ${s.contactName || ''} <${s.contactEmail || ''}>`,
        `Phone: ${s.contactPhone || ''}`,
        `Office: ${s.officeName || ''}`,
        `Office MLS ID: ${s.officeMlsId || ''}`,
        `Membership #: ${s.membershipNumber || ''}`,
        `License #: ${s.licenseNumber || ''}`,
        `Website: ${s.websiteUrl || s.websiteLabel || ''}`,
        `Tenant: ${s.brokurzTenantId}`,
        `Vendor admin: ${this.vendorAdminHref(s)}`,
      ].join('\n')
      try {
        await navigator.clipboard.writeText(text)
        this.actionOk = true
        this.actionMessage = 'Packet copied — paste into Grid admin / ticket.'
      } catch {
        this.actionOk = false
        this.actionMessage = 'Could not copy — select fields manually.'
      }
    },
    async setStatus(status, activateFeed = false) {
      if (!this.selected) return
      this.acting = true
      this.actionMessage = null
      try {
        await this.$apollo.mutate({
          mutation: SET_STATUS,
          variables: {
            id: this.selected.id,
            status,
            activateFeed: Boolean(activateFeed),
            notes: this.opsNotes || null,
          },
        })
        this.actionOk = true
        this.actionMessage =
          status === 'active'
            ? activateFeed
              ? 'Activated connection and set feed active. Run sync so Brokurz sites get live listings.'
              : 'Connection activated. Ensure the board/feed is active and syncing.'
            : `Status set to ${status}.`
        await this.load()
      } catch (err) {
        this.actionOk = false
        this.actionMessage = err.message || 'Update failed'
      } finally {
        this.acting = false
      }
    },
  },
  watch: {
    statusFilter() {
      this.load()
    },
  },
}
</script>

<style scoped>
.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}
.kpi-card {
  border: 1px solid var(--bk-line, #e4e4e7);
  border-radius: 12px;
  background: #fff;
  padding: 0.85rem 1rem;
}
.kpi-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #71717a;
}
.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #18181b;
  line-height: 1.2;
}
.kpi-hint {
  font-size: 0.75rem;
  color: #a1a1aa;
}
.filters {
  display: grid;
  grid-template-columns: 1fr 12rem auto;
  gap: 0.75rem;
  align-items: center;
}
.cell-stack {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #f4f4f5;
  color: #3f3f46;
}
.status-pill[data-status='pending'] {
  background: #fff7ed;
  color: #c2410c;
}
.status-pill[data-status='active'] {
  background: #ecfdf5;
  color: #047857;
}
.status-pill[data-status='suspended'] {
  background: #fef2f2;
  color: #b91c1c;
}
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1.25rem;
  font-size: 0.875rem;
}
.detail-grid .lbl {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #71717a;
  margin-bottom: 0.15rem;
}
.detail-grid .span-2 {
  grid-column: 1 / -1;
}
.field-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
@media (max-width: 900px) {
  .kpi-row {
    grid-template-columns: 1fr 1fr;
  }
  .filters {
    grid-template-columns: 1fr;
  }
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
